import { useCallback } from "react";
import { FieldValues, Path, PathValue, useFormContext } from "react-hook-form";

import { BaseField } from "../BaseField/BaseField.component";
import { FormValuesExploded } from "../formField.types";
import { useNumericField } from "../NumericField/NumericField.hooks";
import { AmountFieldComponent } from "./amountField.component";

type AmountFieldProps<T extends FieldValues> = FormValuesExploded<T> & {
    disabled?: boolean;
    max: number;
    min: number;
    step?: number;
};

const AmountField = <T extends FieldValues>({
    label,
    name,
    microCopy,
    required,
    hideLabel,
    optionalIndication,
    disabled,
    max,
    min,
    step = 1,
    tooltip
}: AmountFieldProps<T>) => {
    const { setValue, getValues } = useFormContext<T>();
    const { onBlurHandler, onChangeHandler } = useNumericField<T>({
        name,
        required,
        defaultValue: min,
        max,
        min
    });

    const onDecrease = useCallback(() => {
        const numericValue = getValues(name);
        const newQuantity = numericValue - step;

        if (newQuantity < min) {
            return;
        }

        setValue(name, newQuantity as PathValue<T, Path<T>>);
    }, [getValues, setValue, name, step, min]);

    const onIncrease = useCallback(() => {
        const numericValue = getValues(name);
        const newQuantity = numericValue + step;

        if (newQuantity > max) {
            return;
        }

        setValue(name, newQuantity as PathValue<T, Path<T>>);
    }, [getValues, setValue, name, step, max]);

    return (
        <BaseField<T, Path<T>>
            label={label}
            name={name}
            microCopy={microCopy}
            required={required}
            hideLabel={hideLabel}
            optionalIndication={optionalIndication}
            tooltip={tooltip}
        >
            {({ field, fieldState }) => {
                const decreaseDisabled = field.value - step < min;
                const increaseDisabled = field.value + step > max;

                return (
                    <AmountFieldComponent
                        {...field}
                        quantityEditable={true}
                        decreaseDisabled={decreaseDisabled}
                        increaseDisabled={increaseDisabled}
                        min={min}
                        max={max}
                        valid={fieldState.isValid}
                        invalid={fieldState.isInvalid}
                        disabled={disabled}
                        onDecrease={onDecrease}
                        onIncrease={onIncrease}
                        onChange={onChangeHandler(field.value, field.onChange)}
                        onBlur={onBlurHandler(field.onBlur)}
                    />
                );
            }}
        </BaseField>
    );
};

export { AmountField };
